import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../config/api';
import QuratorLayout from '../components/qurator/QuratorLayout';
import QureLayout from '../components/qure/QureLayout';
import Swal from 'sweetalert2';

/* ─── Portal Tooltip ─── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);

  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });

  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div className={`profile-tooltip profile-tooltip-${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}>
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

const Profile = () => {
  const { user: authUser, loading: authLoading } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    pin: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const isQure = authUser?.user_type?.toLowerCase() === 'qure';
  const Layout = isQure ? QureLayout : QuratorLayout;

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const apiPrefix = isQure ? '/qure' : '/qurator';
      const response = await API.get(`${apiPrefix}/profile`);
      
      if (response.data.success) {
        setUser(response.data.user);
        setFormData({
          first_name: response.data.user.first_name || '',
          last_name: response.data.user.last_name || '',
          pin: '', // PIN is hashed in the DB — user enters a new value to change it
        });
      } else {
        Swal.fire('Error', 'Failed to load profile', 'error');
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      Swal.fire('Error', 'Failed to load profile', 'error');
    } finally {
      setLoading(false);
    }
  }, [isQure]);

  useEffect(() => {
    if (authUser && !authLoading) {
      fetchUserProfile();
    }
  }, [authUser, authLoading, fetchUserProfile]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name) {
      Swal.fire('Error', 'First name and last name are required', 'error');
      return;
    }

    if (formData.pin && (formData.pin.length < 4 || formData.pin.length > 6)) {
      Swal.fire('Error', 'PIN must be between 4 and 6 digits', 'error');
      return;
    }

    if (formData.pin && !/^\d+$/.test(formData.pin)) {
      Swal.fire('Error', 'PIN must contain only numbers', 'error');
      return;
    }

    setSaving(true);
    try {
      const apiPrefix = isQure ? '/qure' : '/qurator';
      const response = await API.put(`${apiPrefix}/profile`, formData);

      if (response.data.success || response.data.ok) {
        Swal.fire('Success', 'Profile updated successfully', 'success');
        setShowEditModal(false);
        await fetchUserProfile();
      } else {
        Swal.fire('Error', response.data.message || 'Failed to update profile', 'error');
      }
    } catch (error) {
      console.error('Update profile error:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      Swal.fire('Error', 'All password fields are required', 'error');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      Swal.fire('Error', 'New passwords do not match', 'error');
      return;
    }

    if (passwordData.new_password.length < 6) {
      Swal.fire('Error', 'Password must be at least 6 characters long', 'error');
      return;
    }

    setSaving(true);
    try {
      const apiPrefix = isQure ? '/qure' : '/qurator';
      const response = await API.put(`${apiPrefix}/profile/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      if (response.data.success || response.data.ok) {
        Swal.fire('Success', 'Password changed successfully', 'success');
        setShowPasswordModal(false);
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
      } else {
        Swal.fire('Error', response.data.message || 'Failed to change password', 'error');
      }
    } catch (error) {
      console.error('Change password error:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to change password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') {
      Swal.fire('Error', 'Please type DELETE to confirm', 'error');
      return;
    }

    setSaving(true);
    try {
      const apiPrefix = isQure ? '/qure' : '/qurator';
      const response = await API.delete(`${apiPrefix}/profile`);

      if (response.data.success || response.data.ok) {
        Swal.fire('Success', 'Account deleted successfully', 'success').then(() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        });
      } else {
        Swal.fire('Error', response.data.message || 'Failed to delete account', 'error');
      }
    } catch (error) {
      console.error('Delete account error:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to delete account', 'error');
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
      setDeleteConfirm('');
    }
  };


  const getInitials = () => {
    if (!user) return 'U';
    const first = user.first_name?.[0] || '';
    const last = user.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  if (authLoading || loading || !user) {
    const LoadingLayout = isQure ? QureLayout : QuratorLayout;
    return (
      <LoadingLayout>
        <div className="profile-container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading profile...</p>
          </div>
        </div>
      </LoadingLayout>
    );
  }

  return (
    <Layout>
      <div className={`profile-page ${isQure ? 'qure-theme' : 'qurator-theme'}`}>
        {/* Page Header */}
        <div className="profile-page-header">
          <h1><i className="bi bi-person-circle me-3"></i>My Profile</h1>
          <p>Manage your account information and settings</p>
        </div>

        {/* Profile Card */}
        <div className="profile-card">
          {/* Avatar Section */}
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              {getInitials()}
            </div>
            <h2 className="profile-name">
              {user.first_name} {user.last_name}
            </h2>
            <p className="profile-username">@{user.username}</p>
            <span className={`role-badge ${isQure ? 'role-qure' : 'role-qurator'}`}>
              {user.user_type ? user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1) : 'User'}
            </span>
            
            <button 
              className="cool-btn"
              onClick={() => setShowEditModal(true)}
            >
              <i className="bi bi-pencil-square"></i>
              Edit Profile
            </button>
          </div>

          {/* Info Section */}
          <div className="info-section">
            <div className="info-grid">
              <div className="info-item">
                <div className="info-label">
                  <i className="bi bi-person"></i>
                  First Name
                </div>
                <div className="info-value">{user.first_name || 'N/A'}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">
                  <i className="bi bi-person"></i>
                  Last Name
                </div>
                <div className="info-value">{user.last_name || 'N/A'}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">
                  <i className="bi bi-at"></i>
                  Username
                </div>
                <div className="info-value">{user.username || 'N/A'}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">
                  <i className="bi bi-envelope"></i>
                  Email Address
                </div>
                <div className="info-value">{user.email || 'N/A'}</div>
              </div>
              
              <div className="info-item">
                <div className="info-label">
                  <i className="bi bi-shield-check"></i>
                  User Type
                </div>
                <div className="info-value">
                  <span className={`type-badge ${isQure ? 'type-qure' : 'type-qurator'}`}>
                    {user.user_type ? user.user_type.charAt(0).toUpperCase() + user.user_type.slice(1) : 'User'}
                  </span>
                </div>
              </div>
              
              <div className="info-item">
                <div className="info-label">
                  <i className="bi bi-building"></i>
                  Organization Code
                </div>
                <div className="info-value">{user.user_code || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

                {/* Quick Navigation Section */}
        <div className="profile-section-heading mb-3">
          <i className="bi bi-lightning-charge-fill"></i>
          <span>Quick Navigation</span>
        </div>

        <div className="row g-4 mb-4">
          {/* Dashboard Card */}
          <div className="col-12 col-lg-4">
            <div className="profile-nav-card profile-card-dashboard">
              <div className="profile-nav-card-top">
                <div className="profile-nav-card-icon profile-icon-blue">
                  <i className="bi bi-speedometer2"></i>
                </div>
                <div>
                  <h5 className="profile-nav-card-title">Dashboard</h5>
                  <p className="profile-nav-card-desc">
                    View your activity overview, recent experiments, and performance metrics.
                  </p>
                </div>
              </div>
              <div className="profile-nav-card-footer">
                <Tooltip text="Go to your main dashboard">
                  <Link to={isQure ? "/qure-dashboard" : "/qurator-dashboard"} className="profile-nav-btn profile-btn-blue">
                    <i className="bi bi-speedometer2"></i> Open Dashboard
                  </Link>
                </Tooltip>
              </div>
            </div>
          </div>

          {/* Library Card */}
          {!isQure && (
            <div className="col-12 col-lg-4">
              <div className="profile-nav-card profile-card-library">
                <div className="profile-nav-card-top">
                  <div className="profile-nav-card-icon profile-icon-orange">
                    <i className="bi bi-journals"></i>
                  </div>
                  <div>
                    <h5 className="profile-nav-card-title">Experiment Library</h5>
                    <p className="profile-nav-card-desc">
                      Manage your experiment copilots, drafts, and published simulations.
                    </p>
                  </div>
                </div>
                <div className="profile-nav-card-footer">
                  <Tooltip text="Access your experiment library">
                    <Link to="/library" className="profile-nav-btn profile-btn-orange">
                      <i className="bi bi-journals"></i> Open Library
                    </Link>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}

          {/* Team Management Card */}
          {!isQure && (
            <div className="col-12 col-lg-4">
              <div className="profile-nav-card profile-card-team">
                <div className="profile-nav-card-top">
                  <div className="profile-nav-card-icon profile-icon-green">
                    <i className="bi bi-people-fill"></i>
                  </div>
                  <div>
                    <h5 className="profile-nav-card-title">Team Management</h5>
                    <p className="profile-nav-card-desc">
                      Manage team members, roles, and collaboration settings.
                    </p>
                  </div>
                </div>
                <div className="profile-nav-card-footer">
                  <Tooltip text="Manage your team and members">
                    <Link to="/team-management" className="profile-nav-btn profile-btn-green">
                      <i className="bi bi-people"></i> Manage Team
                    </Link>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}

          {/* Qure User - My Experiments Card */}
          {isQure && (
            <div className="col-12 col-lg-4">
              <div className="profile-nav-card profile-card-library">
                <div className="profile-nav-card-top">
                  <div className="profile-nav-card-icon profile-icon-orange">
                    <i className="bi bi-collection-play"></i>
                  </div>
                  <div>
                    <h5 className="profile-nav-card-title">My Experiments</h5>
                    <p className="profile-nav-card-desc">
                      View and run your assigned experiments and simulations.
                    </p>
                  </div>
                </div>
                <div className="profile-nav-card-footer">
                  <Tooltip text="View your assigned experiments">
                    <Link to="/qure-library" className="profile-nav-btn profile-btn-orange">
                      <i className="bi bi-collection-play"></i> My Experiments
                    </Link>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}

          {/* Qure User - Calendar Card */}
          {isQure && (
            <div className="col-12 col-lg-4">
              <div className="profile-nav-card profile-card-team">
                <div className="profile-nav-card-top">
                  <div className="profile-nav-card-icon profile-icon-green">
                    <i className="bi bi-calendar3"></i>
                  </div>
                  <div>
                    <h5 className="profile-nav-card-title">My Calendar</h5>
                    <p className="profile-nav-card-desc">
                      View your scheduled experiments and upcoming sessions.
                    </p>
                  </div>
                </div>
                <div className="profile-nav-card-footer">
                  <Tooltip text="View your experiment calendar">
                    <Link to="/qure-calendar" className="profile-nav-btn profile-btn-green">
                      <i className="bi bi-calendar3"></i> View Calendar
                    </Link>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Security Section */}
        <div className="security-section">
          <h5 className="security-title">
            <i className="bi bi-shield-lock"></i>
            Security Settings
          </h5>
          
          <div className="security-item">
            <div className="security-info">
              <h6>Change Password</h6>
              <p>Keep your account secure with a strong password</p>
            </div>
            <Tooltip text="Update your account password">
              <button 
                type="button" 
                className="security-btn"
                onClick={() => setShowPasswordModal(true)}
              >
                <i className="bi bi-key"></i>
                Change Password
              </button>
            </Tooltip>
          </div>
          
          <div className="security-item">
            <div className="security-info">
              <h6>Delete Account</h6>
              <p>Permanently remove your account and all data</p>
            </div>
            <Tooltip text="Warning: This action cannot be undone">
              <button 
                type="button" 
                className="security-btn danger"
                onClick={() => setShowDeleteModal(true)}
              >
                <i className="bi bi-trash"></i>
                Delete Account
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Edit Profile Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Profile</h3>
                <button className="modal-close" onClick={() => setShowEditModal(false)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <form onSubmit={handleUpdateProfile}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>PIN (4-6 digits)</label>
                    <input
                      type="text"
                      name="pin"
                      value={formData.pin}
                      onChange={handleInputChange}
                      maxLength={6}
                      pattern="[0-9]*"
                      className="form-control"
                      placeholder="Enter PIN (optional)"
                    />
                    <small className="form-text">PIN must be 4-6 digits. Leave empty to keep current PIN.</small>
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={user.email || ''}
                      disabled
                      className="form-control"
                    />
                    <small className="form-text">Email cannot be changed</small>
                  </div>
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      value={user.username || ''}
                      disabled
                      className="form-control"
                    />
                    <small className="form-text">Username cannot be changed</small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowEditModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Change Password Modal */}
        {showPasswordModal && (
          <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Change Password</h3>
                <button className="modal-close" onClick={() => setShowPasswordModal(false)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <form onSubmit={handleChangePassword}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Current Password *</label>
                    <input
                      type="password"
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      required
                      className="form-control"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="form-group">
                    <label>New Password *</label>
                    <input
                      type="password"
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                      className="form-control"
                      placeholder="Enter new password (min 6 characters)"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password *</label>
                    <input
                      type="password"
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      required
                      minLength={6}
                      className="form-control"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div className="form-text">
                    <i className="bi bi-info-circle"></i>
                    Password must be at least 6 characters long for security.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({
                        current_password: '',
                        new_password: '',
                        confirm_password: ''
                      });
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={saving}
                  >
                    {saving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content modal-danger" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Account</h3>
                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="warning-box">
                  <i className="bi bi-exclamation-triangle"></i>
                  <h4>Warning: This action cannot be undone!</h4>
                  <p>Deleting your account will permanently remove all your data, including:</p>
                  <ul>
                    <li>Your profile information</li>
                    <li>All your experiments and protocols</li>
                    <li>Your team memberships</li>
                    <li>All scheduled experiments</li>
                  </ul>
                  <p className="confirm-text">
                    Type <strong>DELETE</strong> to confirm:
                  </p>
                  <input
                    type="text"
                    className="form-control"
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="Type DELETE to confirm"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirm('');
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={handleDeleteAccount}
                  disabled={saving || deleteConfirm !== 'DELETE'}
                >
                  {saving ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
